import type { DashboardWidget } from '../DashboardGrid';

export type DashboardViewMode = 'admin' | 'client' | 'employee';

export interface WidgetLayoutDefinition {
  id: string;
  title: string;
  description: string;
  category: 'overview' | 'tasks' | 'analytics' | 'communication';
  defaultSize: { w: number; h: number };
  minSize: { w: number; h: number };
  maxSize?: { w: number; h: number };
  availableFor: DashboardViewMode[];
  deletable: boolean;
}

export const WIDGET_LAYOUT_DEFINITIONS: WidgetLayoutDefinition[] = [
  {
    id: 'health-status',
    title: 'Health Status',
    description: 'Overall project health and progress indicator',
    category: 'overview',
    defaultSize: { w: 4, h: 4 },
    minSize: { w: 4, h: 4 },
    maxSize: { w: 6, h: 6 },
    availableFor: ['admin', 'client', 'employee'],
    deletable: true,
  },
  {
    id: 'metrics',
    title: 'Project Metrics',
    description: 'Key project statistics and performance indicators',
    category: 'analytics',
    defaultSize: { w: 8, h: 4 },
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    availableFor: ['admin', 'client', 'employee'],
    deletable: true,
  },
  {
    id: 'timeline',
    title: 'Milestone Timeline',
    description: 'Complete timeline view of all project milestones with status indicators',
    category: 'overview',
    defaultSize: { w: 12, h: 6 },
    minSize: { w: 6, h: 6 },
    maxSize: { w: 18, h: 12 },
    availableFor: ['admin', 'client', 'employee'],
    deletable: true,
  },
  {
    id: 'opportunities',
    title: 'Opportunities to Accelerate',
    description: 'Identify tasks and opportunities for project acceleration',
    category: 'analytics',
    defaultSize: { w: 12, h: 5 },
    minSize: { w: 6, h: 5 },
    maxSize: { w: 18, h: 10 },
    availableFor: ['admin', 'employee'],
    deletable: true,
  },
  {
    id: 'progress-trend',
    title: 'Progress Trend',
    description: 'Track project progress over time with trend analysis',
    category: 'analytics',
    defaultSize: { w: 4, h: 4 },
    minSize: { w: 4, h: 4 },
    maxSize: { w: 8, h: 8 },
    availableFor: ['admin', 'client', 'employee'],
    deletable: true,
  },
  {
    id: 'recent-activity',
    title: 'Recent Activity',
    description: 'Latest project updates and changes',
    category: 'overview',
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 6, h: 5 },
    maxSize: { w: 12, h: 10 },
    availableFor: ['admin', 'client', 'employee'],
    deletable: true,
  },
  {
    id: 'meeting-notes',
    title: 'Meeting Notes',
    description: 'Recent meeting notes and internal-only memo records',
    category: 'communication',
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 6, h: 5 },
    maxSize: { w: 12, h: 8 },
    availableFor: ['admin', 'client', 'employee'],
    deletable: true,
  },
  {
    id: 'client-actions',
    title: 'Client Action Items',
    description: 'Items requiring client attention and action',
    category: 'tasks',
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 6, h: 5 },
    maxSize: { w: 12, h: 10 },
    availableFor: ['admin', 'client', 'employee'],
    deletable: true,
  },
  {
    id: 'project-contact',
    title: 'Project Contact',
    description: 'Contact information and quick communication actions',
    category: 'communication',
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 6, h: 3 },
    maxSize: { w: 12, h: 6 },
    availableFor: ['admin', 'client', 'employee'],
    deletable: true,
  },
];

export const getWidgetLayoutDefinition = (id: string): WidgetLayoutDefinition | undefined =>
  WIDGET_LAYOUT_DEFINITIONS.find((widget) => widget.id === id);

export const DEFAULT_WIDGET_CONFIGS: Record<DashboardViewMode, string[]> = {
  admin: [
    'health-status',
    'progress-trend',
    'metrics',
    'timeline',
    'opportunities',
    'client-actions',
    'recent-activity',
    'meeting-notes',
  ],
  client: [
    'health-status',
    'progress-trend',
    'metrics',
    'timeline',
    'client-actions',
    'recent-activity',
    'meeting-notes',
    'project-contact',
  ],
  employee: [
    'health-status',
    'progress-trend',
    'metrics',
    'timeline',
    'opportunities',
    'client-actions',
    'recent-activity',
    'meeting-notes',
  ],
};

export const getLayoutValidationWidgets = (viewMode: DashboardViewMode): DashboardWidget[] =>
  WIDGET_LAYOUT_DEFINITIONS.filter(definition => definition.availableFor.includes(viewMode)).map(definition => ({
    id: definition.id,
    component: null,
    defaultSize: definition.defaultSize,
    minSize: definition.minSize,
    maxSize: definition.maxSize,
    deletable: definition.deletable,
    title: definition.title,
  }));
